/**
 * Player Ship - Mouse-controlled player ship with physics and combat
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { PhysicsSystem } from '../systems/PhysicsSystem.js';
import { GAME_BOUNDARY_SIZE } from '../utils/constants.js';
import {
    getLevelData,
    LEVEL_CAP,
    getPlayerModelForLevel,
    getUpgradeCostForLevel
} from '../config/shipLevels.js';

export class PlayerShip {
    constructor(options = {}) {
        this.game = options.game;
        this.position = options.position || new THREE.Vector3(0, 0, 0);
        this.id = 'player';
        this.faction = 'player';
        this.isPlayer = true;
        this.lastAttacker = null;

        // Upgrade system
        this.level = 1;
        this.score = 0;
        this.isUpgrading = false;

        // Ship properties（由等级数据驱动）
        const levelData = getLevelData(this.level);
        this.health = levelData.health;
        this.maxHealth = levelData.health;
        this.speed = 15;
        this.rotationSpeed = 3;

        // Combat properties
        this.cannonCooldown = 1.2; // H5自动射击，保持原来的快速射击频率
        this.lastFireTime = 0;
        this.cannonRange = 26;
        this.cannonballSize = 0.8;
        this.cannonballDamage = levelData.attack;
        this.autoFireCooldown = 1.2;
        this.lastAutoFireTime = 0;

        // Visual feedback control
        this.isFlashing = false;
        // Throttle for building (island) hit effects to avoid spamming
        this.lastBuildingHitEffect = 0;
        this.buildingEffectCooldown = 220; // ms

        // Movement
        this.targetPosition = new THREE.Vector3();
        this.currentVelocity = new THREE.Vector3();
        this.maxVelocity = this.speed;
        this.acceleration = 45; // 提升加速度，响应更灵敏
        this.deceleration = 12; // 备用参数

        // Three.js objects
        this.mesh = null;
        this.cannonMesh = null;

        // Cannon.js physics
        this.body = null;

        // Movement smoothing
        this.moveThreshold = 3.0; // 提前进入刹车区，减小惯性
        this.rotationThreshold = 5.0; // Minimum distance before rotating
        this.isMoving = false;

        // Rotation control (快速响应)
        this.rotationSpring = 200;
        this.rotationDamping = 15;
        this.rotationMaxTorque = 300;

        // Mouse sensitivity control
        this.lastTargetUpdate = 0;
        this.targetUpdateInterval = 16; // ms between target updates (~60fps)
        this.smoothedTarget = new THREE.Vector3();
        this.lastLookDirection = new THREE.Vector3(0, 0, 1);

        // Health bar will be created after mesh is loaded
        this.lastHitEffect = 0;
        this.hitEffectCooldown = 120; // ms

        // Cached textures/materials for effects
        this._fireTexture = null;

        // Auto-fire configuration for touch controls
        this.autoFireEnabled = true;
        this.autoFireRangeMultiplier = 1.0;
        this.joystickTargetDistance = 45;
        this.usingJoystick = false;

        // Shared attack target state (用于友军同步攻击目标)
        this.currentAttackTarget = null;
        this.currentAttackTargetTime = 0;
        this.attackTargetRetention = 3600; // ms
    }

    async init() {
        await this.createMesh(getPlayerModelForLevel(this.level));
        this.applyLevelStats(this.level);
        this.createPhysicsBody();
        this.setInitialPosition();
        this.createHealthBar();
        this.updateHealthBar();
        if (this.game && this.game.sceneManager) {
            this.adjustCameraForUpgrade(this.level);
        }
    }

    createBasicMesh() {
        // Create simple ship hull (fallback)
        const hullGeometry = new THREE.BoxGeometry(2, 0.8, 4);
        const hullMaterial = new THREE.MeshLambertMaterial({
            color: 0x0066CC // Blue for player
        });

        this.mesh = new THREE.Mesh(hullGeometry, hullMaterial);
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;

        // 船头标识 (红色尖头) - 指向+Z方向
        const bowGeometry = new THREE.ConeGeometry(0.8, 1.5);
        const bowMaterial = new THREE.MeshLambertMaterial({
            color: 0xFF0000 // 红色船头
        });
        const bowMesh = new THREE.Mesh(bowGeometry, bowMaterial);
        bowMesh.position.set(0, 0.8, 2.5); // 船的前端(+Z方向)
        bowMesh.rotateX(Math.PI / 2); // 让锥体指向前方
        this.mesh.add(bowMesh);

        // Add player tag for identification
        this.mesh.userData = { type: 'player', entity: this };

        // Set initial position
        this.mesh.position.copy(this.position);
    }

    createCannon() {
        // Create cannon geometry
        const cannonGeometry = new THREE.CylinderGeometry(0.15, 0.2, 1.5);
        const cannonMaterial = new THREE.MeshLambertMaterial({
            color: 0x444444 // Dark gray cannon
        });

        this.cannonMesh = new THREE.Mesh(cannonGeometry, cannonMaterial);
        this.cannonMesh.rotation.z = Math.PI / 2; // Rotate to point forward
        this.cannonMesh.position.set(0.8, 0.6, 1); // Right side of ship
        this.cannonMesh.castShadow = true;

        this.mesh.add(this.cannonMesh);

        // Create cannon on left side too
        const leftCannonMesh = this.cannonMesh.clone();
        leftCannonMesh.position.set(-0.8, 0.6, 1); // Left side of ship
        this.mesh.add(leftCannonMesh);
    }

    createPhysicsBody() {
        // 根据船只等级确定物理体大小
        let size = { width: 3, height: 1, length: 6 }; // L1-L3
        if (this.level >= 4 && this.level <= 6) {
            size = { width: 4.2, height: 1.2, length: 8.5 };
        } else if (this.level >= 7) {
            size = { width: 5.5, height: 1.4, length: 11 };
        }

        // Create physics body using the physics system
        this.body = this.game.physicsSystem.createShipBody(this.position, size);

        // Set collision group for player (使用正确的碰撞组)
        this.body.collisionFilterGroup = 2; // 玩家船只碰撞组
        this.body.collisionFilterMask = 1 | 2 | 4 | 8; // 恢复与敌船/副船碰撞，保持对战反馈

        // Add entity reference for damage detection
        this.body.userData = { type: 'player', entity: this };

        // Add collision event listener
        this.body.addEventListener('collide', (event) => {
            this.handleCollision(event);
        });

        // 将物理体添加到物理世界中 - 这是关键步骤！
        this.game.physicsSystem.addBody(this.body);

        this.updateMovementProfile(this.level);
    }

    setInitialPosition() {
        this.mesh.position.copy(this.position);
        this.body.position.set(this.position.x, this.position.y, this.position.z);
        this.targetPosition.copy(this.position);
        this.smoothedTarget.copy(this.position); // Initialize smoothed target
        this.lastLookDirection.set(0, 0, 1);
    }

    update(deltaTime) {
        // 如果正在升级，暂停移动和旋转更新
        if (this.isUpgrading) {
            // 只同步位置，不更新移动
            PhysicsSystem.syncMeshWithBody(this.mesh, this.body);
            return;
        }

        // 实时监控属性变化（每10秒输出一次）
        if (!this._lastLogTime || Date.now() - this._lastLogTime > 10000) {
            this._lastLogTime = Date.now();
            console.log(`Ship Level ${this.level} Stats:`, {
                speed: this.speed,
                maxVelocity: this.maxVelocity,
                cannonballDamage: this.cannonballDamage,
                cannonCooldown: this.cannonCooldown,
                bodyDamping: this.body ? this.body.linearDamping : 'no body'
            });
        }

        // Update movement towards target
        this.updateMovement(deltaTime);

        // Auto-aim and fire when using touch controls
        this.handleAutoFire();

        // Update physics body rotation to match movement direction
        this.updateRotation(deltaTime);

        // Sync Three.js mesh with physics body
        PhysicsSystem.syncMeshWithBody(this.mesh, this.body);

        // Ensure ship stays on water surface (Y = 0.1 to match ocean level)
        if (this.mesh) {
            this.mesh.position.y = 0.1;
        }
        if (this.body) {
            this.body.position.y = 0.1;
        }

        // 让血条始终正对相机（玩家保持原规则）
        if (this.healthBarContainer && this.game && this.game.sceneManager && this.game.sceneManager.camera) {
            this.healthBarContainer.lookAt(this.game.sceneManager.camera.position);
        }

        // Update cannon cooldown
        this.updateWeapons(deltaTime);
    }

    updateMovement(deltaTime) {
        if (!this.body) return;

        // 边界检查 - 强制保持在游戏区域内
        this.enforceGameBoundaries();

        // Calculate distance to target
        const currentPos = new THREE.Vector3().copy(this.body.position);
        this.applyJoystickControl(currentPos);
        const distanceToTarget = currentPos.distanceTo(this.targetPosition);

        // Check if we're close enough to the target
        if (distanceToTarget < this.moveThreshold) {
            this.isMoving = false;
            // 极强的刹车，几乎立即停止
            this.body.velocity.x *= 0.3;
            this.body.velocity.z *= 0.3;
            const planarSpeed = Math.hypot(this.body.velocity.x, this.body.velocity.z);
            if (planarSpeed < 0.2) {
                this.body.velocity.x = 0;
                this.body.velocity.z = 0;
            }
            return;
        }

        this.isMoving = true;

        // Calculate direction to target
        const direction = new THREE.Vector3()
            .subVectors(this.targetPosition, currentPos)
            .normalize();

        // Apply movement force with distance-based scaling
        const distanceScale = Math.min(distanceToTarget / 10, 1);
        // 更强的推进力，近距离仍保有一定推力
        const forceStrength = this.acceleration * this.body.mass * (0.6 + 0.4 * distanceScale);

        this.game.physicsSystem.applyShipMovementForce(
            this.body,
            direction,
            forceStrength
        );

        // Limit maximum velocity (X-Z plane for 2D game)
        const velocity = this.body.velocity;
        const currentSpeed = Math.sqrt(velocity.x * velocity.x + velocity.z * velocity.z);

        if (currentSpeed > this.maxVelocity) {
            const scale = this.maxVelocity / currentSpeed;
            velocity.x *= scale;
            velocity.z *= scale;
        }

        // Keep Y velocity at 0 (no movement in Y axis, stay at sea level)
        velocity.y = 0;
    }

    handleAutoFire() {
        if (!this.autoFireEnabled || !this.game || !this.game.enemyManager) return;

        const inputManager = this.game.inputManager;
        const isTouchControl = inputManager && (inputManager.isJoystickEnabled() || inputManager.lastInputType === 'touch' || (inputManager.touch && inputManager.touch.isActive));
        if (!isTouchControl) {
            return;
        }

        const playerPosition = this.body ? this.body.position : (this.mesh ? this.mesh.position : null);
        if (!playerPosition) return;

        const nearest = this.findClosestEnemy();
        if (!nearest) return;

        const maxRange = this.cannonRange * (this.autoFireRangeMultiplier || 1.0);
        if (nearest.distance > maxRange) {
            return;
        }

        this.setAttackTarget(nearest.enemy);

        const enemyPos = nearest.enemyPosition;
        if (!enemyPos) return;

        const aimDirection = new THREE.Vector3(
            enemyPos.x - playerPosition.x,
            0,
            enemyPos.z - playerPosition.z
        );

        if (aimDirection.lengthSq() === 0) {
            return;
        }

        aimDirection.normalize();
        this.lastLookDirection.copy(aimDirection);

        if (this.lastAutoFireTime > 0) {
            return;
        }

        const fired = this.shoot(aimDirection);
        if (fired) {
            this.lastAutoFireTime = this.autoFireCooldown;
        }
    }

    findClosestEnemy() {
        if (!this.game || !this.game.enemyManager || !this.game.enemyManager.enemies) return null;

        const playerPos = this.body ? this.body.position : (this.mesh ? this.mesh.position : null);
        if (!playerPos) return null;

        let closestEnemy = null;
        let closestDistance = Infinity;
        let closestPosition = null;

        this.game.enemyManager.enemies.forEach((enemy) => {
            if (!enemy || enemy.shouldRemove || enemy.health <= 0) return;

            const enemyBody = enemy.body;
            const enemyPos = enemyBody && enemyBody.position ? enemyBody.position : (enemy.mesh ? enemy.mesh.position : null);
            if (!enemyPos) return;

            const dx = enemyPos.x - playerPos.x;
            const dz = enemyPos.z - playerPos.z;
            const distance = Math.sqrt(dx * dx + dz * dz);

            if (!Number.isFinite(distance)) return;

            if (distance < closestDistance) {
                closestDistance = distance;
                closestEnemy = enemy;
                closestPosition = enemyPos;
            }
        });

        if (!closestEnemy) {
            return null;
        }

        return {
            enemy: closestEnemy,
            enemyPosition: closestPosition,
            distance: closestDistance
        };
    }

    setAttackTarget(enemy) {
        if (!enemy) {
            this.clearAttackTarget();
            return;
        }

        this.currentAttackTarget = enemy;
        const now = (typeof performance !== 'undefined' && typeof performance.now === 'function')
            ? performance.now()
            : Date.now();
        this.currentAttackTargetTime = now;
    }

    clearAttackTarget(enemy = null) {
        if (enemy && enemy !== this.currentAttackTarget) {
            return;
        }

        this.currentAttackTarget = null;
        this.currentAttackTargetTime = 0;
    }

    getCurrentAttackTarget(maxAgeMs = this.attackTargetRetention) {
        const enemy = this.currentAttackTarget;
        if (!enemy) {
            return null;
        }

        if (enemy.shouldRemove || enemy.health <= 0) {
            this.clearAttackTarget(enemy);
            return null;
        }

        if (enemy.id && this.game?.enemyManager?.enemies instanceof Map) {
            if (!this.game.enemyManager.enemies.has(enemy.id)) {
                this.clearAttackTarget(enemy);
                return null;
            }
        }

        if (maxAgeMs && this.currentAttackTargetTime) {
            const now = (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now();
            if (now - this.currentAttackTargetTime > maxAgeMs) {
                this.clearAttackTarget(enemy);
                return null;
            }
        }

        return enemy;
    }

    findEnemyAlongDirection(direction, maxDistance = this.cannonRange * 1.25, maxAngleDeg = 40) {
        if (!direction || direction.lengthSq() === 0) {
            return null;
        }

        if (!this.game?.enemyManager?.enemies || this.game.enemyManager.enemies.size === 0) {
            return null;
        }

        const playerPos = this.body ? this.body.position : (this.mesh ? this.mesh.position : null);
        if (!playerPos) {
            return null;
        }

        const dir = direction.clone();
        if (dir.lengthSq() === 0) {
            return null;
        }
        dir.normalize();

        const cosThreshold = Math.cos(THREE.MathUtils.degToRad(maxAngleDeg));
        const toEnemy = new THREE.Vector3();

        let best = null;

        this.game.enemyManager.enemies.forEach((enemy) => {
            if (!enemy || enemy.shouldRemove || enemy.health <= 0) {
                return;
            }

            const enemyPos = enemy.body?.position || enemy.mesh?.position;
            if (!enemyPos) {
                return;
            }

            const dx = enemyPos.x - playerPos.x;
            const dz = enemyPos.z - playerPos.z;
            const distance = Math.sqrt(dx * dx + dz * dz);

            if (!Number.isFinite(distance) || distance <= 0.001 || distance > maxDistance) {
                return;
            }

            toEnemy.set(dx, 0, dz).normalize();
            const dot = dir.dot(toEnemy);

            if (dot < cosThreshold) {
                return;
            }

            const alignmentScore = distance - dot * 2;

            if (!best || alignmentScore < best.score) {
                best = {
                    enemy,
                    distance,
                    score: alignmentScore
                };
            }
        });

        return best;
    }

    acquireAttackTargetFromDirection(direction) {
        const candidate = this.findEnemyAlongDirection(direction);
        if (candidate && candidate.enemy) {
            this.setAttackTarget(candidate.enemy);
        } else {
            this.getCurrentAttackTarget();
        }
    }

    applyJoystickControl(currentPos) {
        const inputManager = this.game ? this.game.inputManager : null;
        if (!inputManager || !inputManager.isJoystickEnabled()) {
            this.usingJoystick = false;
            return false;
        }

        this.usingJoystick = true;
        const vector = inputManager.getJoystickVector();
        const magnitude = Math.sqrt(vector.x * vector.x + vector.y * vector.y);

        if (!inputManager.isJoystickActive() || magnitude < 0.05) {
            this.targetPosition.copy(currentPos);
            return true;
        }

        const direction = new THREE.Vector3(vector.x, 0, vector.y);
        if (direction.lengthSq() === 0) {
            this.targetPosition.copy(currentPos);
            return true;
        }

        direction.normalize();
        this.lastLookDirection.copy(direction);

        const distanceScale = Math.min(1, magnitude);
        const targetDistance = 5 + distanceScale * this.joystickTargetDistance;

        this.targetPosition.set(
            currentPos.x + direction.x * targetDistance,
            0,
            currentPos.z + direction.z * targetDistance
        );

        return true;
    }

    updateRotation(deltaTime) {
        if (!this.body) return;

        const maxAngularVel = 3.0;
        const currentAngularVel = this.body.angularVelocity.y;


        if (Math.abs(currentAngularVel) > maxAngularVel) {
            this.body.angularVelocity.y = Math.sign(currentAngularVel) * maxAngularVel;
        }

        // Always allow rotation to face mouse, regardless of movement
        // Comment out the movement check for now

        // Get current ship orientation (X-Z平面的2D角度)
        const forward = new THREE.Vector3(0, 0, 1); // Z轴正方向为前进方向
        forward.applyQuaternion(this.body.quaternion);
        const currentAngle = Math.atan2(forward.x, forward.z);

        // Get target angle from stored direction (X-Z平面)
        const targetAngle = Math.atan2(this.lastLookDirection.x, this.lastLookDirection.z);

        let angleDiff = targetAngle - currentAngle;
        while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
        while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

        // 移除角度阈值，让船头始终跟随鼠标

        // 直接设置船只角度，绕Y轴旋转（正确的2D旋转）
        const targetQuaternion = new THREE.Quaternion();
        targetQuaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), targetAngle); // Y轴旋转用于X-Z平面

        // 直接设置物理体的旋转
        this.body.quaternion.set(
            targetQuaternion.x,
            targetQuaternion.y,
            targetQuaternion.z,
            targetQuaternion.w
        );

        // 停止角速度
        this.body.angularVelocity.set(0, 0, 0);

    }

    // 强制边界检查 - 确保船只不会越过红色边界
    enforceGameBoundaries() {
        if (!this.body || !this.game) return;

        const bounds = this.game.getGameBounds();
        const position = this.body.position;
        const margin = 2; // 在边界内留出2单位的安全距离

        let corrected = false;

        // 检查X轴边界
        if (position.x < bounds.minX + margin) {
            position.x = bounds.minX + margin;
            this.body.velocity.x = Math.max(0, this.body.velocity.x); // 停止向边界移动
            corrected = true;
        } else if (position.x > bounds.maxX - margin) {
            position.x = bounds.maxX - margin;
            this.body.velocity.x = Math.min(0, this.body.velocity.x); // 停止向边界移动
            corrected = true;
        }

        // 检查Z轴边界
        if (position.z < bounds.minZ + margin) {
            position.z = bounds.minZ + margin;
            this.body.velocity.z = Math.max(0, this.body.velocity.z); // 停止向边界移动
            corrected = true;
        } else if (position.z > bounds.maxZ - margin) {
            position.z = bounds.maxZ - margin;
            this.body.velocity.z = Math.min(0, this.body.velocity.z); // 停止向边界移动
            corrected = true;
        }

    }

    updateWeapons(deltaTime) {
        // Update cooldowns
        if (this.lastFireTime > 0) {
            this.lastFireTime -= deltaTime;
        }

        if (this.lastAutoFireTime > 0) {
            this.lastAutoFireTime -= deltaTime;
            if (this.lastAutoFireTime < 0) {
                this.lastAutoFireTime = 0;
            }
        }
    }

    // Set target position for mouse control
    setTargetPosition(mousePosition) {
        if (!this.game || !this.game.sceneManager) return;
        if (this.game.inputManager && this.game.inputManager.isJoystickEnabled()) {
            return;
        }

        // 计算鼠标相对于屏幕中心（船体）的方向
        const shipScreenPos = {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2
        };

        const mouseRelative = {
            x: mousePosition.x - shipScreenPos.x,
            y: mousePosition.y - shipScreenPos.y
        };

        // 计算鼠标相对船体的角度（船头朝向）
        const targetAngle = Math.atan2(-mouseRelative.y, mouseRelative.x) + Math.PI / 2;

        // 只有角度变化超过阈值时才更新船头朝向
        const currentAngle = Math.atan2(this.lastLookDirection.x, this.lastLookDirection.z);
        let angleDiff = targetAngle - currentAngle;
        while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
        while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

        // 降低阈值让朝向更跟手
        if (Math.abs(angleDiff) > Math.PI / 60) { // 3度阈值
            this.lastLookDirection.x = Math.sin(targetAngle);
            this.lastLookDirection.y = 0;
            this.lastLookDirection.z = Math.cos(targetAngle);

        }

        // 计算移动目标：船体位置 + 船头方向 * 固定距离（持续移动）
        if (this.body) {
            const currentShipPos = this.body.position;
            const distance = Math.sqrt(mouseRelative.x * mouseRelative.x + mouseRelative.y * mouseRelative.y);

            // PC端：鼠标在屏幕上时，持续向鼠标方向移动
            // 使用固定的前方距离，让船持续追踪鼠标
            const moveDistance = Math.max(distance * 0.15, 15); // 至少15单位距离，保持持续移动

            // 目标位置 = 当前位置 + 船头方向 * 移动距离
            this.targetPosition.set(
                currentShipPos.x + this.lastLookDirection.x * moveDistance,
                0,
                currentShipPos.z + this.lastLookDirection.z * moveDistance
            );
        }

    }

    // Shooting method
    shoot(directionOverride = null) {
        // Check cooldown
        if (this.lastFireTime > 0) {
            return false;
        }

        // Reset cooldown
        this.lastFireTime = this.cannonCooldown;

        // Create projectile
        this.createProjectile(directionOverride);

        return true;
    }

    createProjectile(directionOverride = null) {
        const weaponOrigin = new THREE.Vector3();
        if (this.cannonMesh) {
            this.cannonMesh.getWorldPosition(weaponOrigin);
        } else {
            this.mesh.getWorldPosition(weaponOrigin);
        }

        let firingDirection;
        if (directionOverride) {
            firingDirection = directionOverride.clone().normalize();
        } else {
            firingDirection = new THREE.Vector3(0, 0, 1);
            firingDirection.applyQuaternion(this.mesh.quaternion);
            firingDirection.normalize();
        }

        this.acquireAttackTargetFromDirection(firingDirection);

        const spawnPosition = weaponOrigin.clone().add(firingDirection.clone().multiplyScalar(5));

        // Create actual cannonball projectile
        this.createCannonball(spawnPosition, firingDirection);

        // Create muzzle flash effect
        this.createMuzzleFlash(firingDirection);
    }

    createMuzzleFlash(firingDirection = null) {
        // Simple muzzle flash effect
        const flashGeometry = new THREE.SphereGeometry(0.5);
        const flashMaterial = new THREE.MeshBasicMaterial({
            color: 0xFFAA00,
            transparent: true,
            opacity: 0.8
        });

        const flash = new THREE.Mesh(flashGeometry, flashMaterial);

        // Determine flash position slightly ahead of the cannon
        const flashPosition = new THREE.Vector3();
        if (this.cannonMesh) {
            this.cannonMesh.getWorldPosition(flashPosition);
        } else {
            this.mesh.getWorldPosition(flashPosition);
        }

        const direction = firingDirection ? firingDirection.clone() : new THREE.Vector3(0, 0, 1).applyQuaternion(this.mesh.quaternion);
        if (direction.lengthSq() > 0) {
            direction.normalize();
            flashPosition.add(direction.multiplyScalar(3));
        }
        flash.position.copy(flashPosition);

        // Add to scene
        this.game.sceneManager.add(flash);

        // Animate and remove
        let scale = 1;
        const animate = () => {
            scale += 0.2;
            flashMaterial.opacity -= 0.1;
            flash.scale.setScalar(scale);

            if (flashMaterial.opacity > 0) {
                requestAnimationFrame(animate);
            } else {
                this.game.sceneManager.remove(flash);
                flashGeometry.dispose();
                flashMaterial.dispose();
            }
        };
        animate();
    }

    createCannonball(position, direction) {
        // 创建物理炮弹而不是动画炮弹
        const cannonball = this.createPhysicsCannonball(position, direction);

        // 添加到游戏对象管理器
        const cannonballId = `cannonball_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.game.addGameObject(cannonballId, cannonball);

        // 设置自动清理定时器
        setTimeout(() => {
            this.game.removeGameObject(cannonballId);
        }, 10000); // 10秒后自动清理

    }

    createPhysicsCannonball(position, direction) {
        // 简单红色炮弹
        const cannonballGeometry = new THREE.SphereGeometry(0.6, 12, 12);
        const cannonballMaterial = new THREE.MeshLambertMaterial({
            color: 0xFF0000 // 红色
        });

        const mesh = new THREE.Mesh(cannonballGeometry, cannonballMaterial);
        mesh.position.copy(position);
        mesh.castShadow = true; // 投射阴影

        // 创建物理体 (半径与视觉匹配)
        const body = this.game.physicsSystem.createProjectileBody(position, this.cannonballSize);

        // 正常设置与所有对象的碰撞
        body.collisionFilterMask = 1 | 2 | 8; // 与边界(1)、船只(2)、建筑物(8)碰撞

        // 炮弹速度与敌船一致：24 + level * 1.5
        const baseSpeed = 24 + this.level * 1.5;
        const velocity = direction.clone().multiplyScalar(baseSpeed);
        body.velocity.set(velocity.x, velocity.y, velocity.z);

        // 记录发射者，用于排除自身碰撞
        body.userData = {
            type: 'cannonball',
            shooter: this.body, // 记录发射者的物理体引用
            shooterMesh: this.mesh, // 记录发射者的网格引用
            damage: this.cannonballDamage // 记录伤害值
        };

        // 添加碰撞事件监听
        body.addEventListener('collide', (event) => {
            this.handleCannonballCollision(event, mesh, body);
        });

        const boundaryLimit = GAME_BOUNDARY_SIZE;

        // 创建炮弹对象
        const cannonballObject = {
            mesh: mesh,
            body: body,
            lifeTime: 0,
            maxLifeTime: 10, // 10秒生命周期

            update: (deltaTime) => {
                // 同步网格和物理体
                if (mesh && body) {
                    this.game.physicsSystem.constructor.syncMeshWithBody(mesh, body);
                }

                // 更新生命周期
                cannonballObject.lifeTime += deltaTime;

                // 检查是否超出边界或生命周期结束
                if (cannonballObject.lifeTime > cannonballObject.maxLifeTime ||
                    Math.abs(body.position.x) > boundaryLimit ||
                    Math.abs(body.position.z) > boundaryLimit) {

                    // 创建爆炸效果
                    this.createExplosion(new THREE.Vector3().copy(body.position));

                    // 标记为需要移除
                    cannonballObject.shouldRemove = true;
                }
            },

            dispose: () => {
                if (mesh) {
                    // 清理几何体和材质
                    mesh.traverse((child) => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) {
                            if (Array.isArray(child.material)) {
                                child.material.forEach(mat => mat.dispose());
                            } else {
                                child.material.dispose();
                            }
                        }
                    });
                }
            }
        };

        return cannonballObject;
    }

    // 处理炮弹碰撞事件
    handleCannonballCollision(event, mesh, body) {
        const otherBody = event.target === body ? event.body : event.target;

        // 检查是否与发射者自身碰撞，如果是则忽略
        if (body.userData && body.userData.shooter === otherBody) {
            return; // 忽略与发射者的碰撞
        }

        // 创建爆炸效果
        this.createExplosion(new THREE.Vector3().copy(body.position));

        // 检查碰撞对象类型
        const collisionGroup = otherBody.collisionFilterGroup;

        if (collisionGroup === 2) {
            // 敌船碰撞效果（强度根据命中后血量预测）
            let intensity = 0.6;
            if (otherBody.userData && otherBody.userData.entity) {
                const enemy = otherBody.userData.entity;
                const damage = body.userData.damage || 2; // 使用炮弹的实际伤害值
                const remaining = Math.max(0, (enemy.health ?? enemy.maxHealth) - damage);
                const ratio = enemy.maxHealth ? remaining / enemy.maxHealth : 0.5;
                const lethal = remaining <= 0;
                intensity = lethal ? 0.85 : Math.max(0.15, Math.min(0.7, 0.15 + (1 - ratio) * 0.4));
                this.createShipHitEffect(new THREE.Vector3().copy(body.position), { intensity, lethal });

                // 对敌船造成伤害
                if (enemy.takeDamage) {
                    enemy.takeDamage(damage, this); // 使用炮弹的实际伤害值
                    console.log(`Player cannonball hit enemy with ${damage} damage (level ${this.level})`); // 调试信息

                    // Play hit sound effect
                    if (this.game && this.game.audioManager) {
                        this.game.audioManager.playCannonHit();
                    }

                    if (enemy.health <= 0) {
                        this.clearAttackTarget(enemy);
                    }
                }
            } else {
                this.createShipHitEffect(new THREE.Vector3().copy(body.position), { intensity: 0.2 });
            }
        } else if (collisionGroup === 8) {
            // 建筑物碰撞效果
            this.createBuildingHitEffect(new THREE.Vector3().copy(body.position));
        }

        // 通过查找对应的炮弹对象并标记为删除
        this.game.gameObjects.forEach((gameObject, id) => {
            if (gameObject.body === body) {
                gameObject.shouldRemove = true;
            }
        });
    }

    createExplosion(position) {
        // 创建优化的不规则黑烟扩散效果，减少性能开销
        const smokeCount = Math.max(2, Math.floor(Math.random() * 3) + 2); // 减少粒子数量到2-4个
        
        for (let i = 0; i < smokeCount; i++) {
            // 创建简化的烟雾粒子
            const smokeSize = 0.4 + Math.random() * 0.6; // 保持1.5单位左右的规模
            // 使用更低的细分度减少顶点数量
            const smokeGeometry = new THREE.SphereGeometry(smokeSize, 4, 3);
            const smokeMaterial = new THREE.MeshBasicMaterial({
                color: 0x333333, // 深灰色黑烟
                transparent: true,
                opacity: 0.3 + Math.random() * 0.2 // 简化透明度范围
            });

            const smoke = new THREE.Mesh(smokeGeometry, smokeMaterial);
            smoke.position.copy(position);
            
            // 简化初始位置偏移
            smoke.position.x += (Math.random() - 0.5) * 1.5;
            smoke.position.z += (Math.random() - 0.5) * 1.5;
            smoke.position.y += Math.random() * 0.3;

            this.game.sceneManager.add(smoke);

            // 简化动画参数
            let scale = 0.2 + Math.random() * 0.3;
            let opacity = 0.2 + Math.random() * 0.15;
            const maxScale = 1.2 + Math.random() * 0.5; // 稍微减小最大规模
            const scaleSpeed = 0.015 + Math.random() * 0.015; // 稍微减慢扩散速度
            const opacityDecay = 0.008 + Math.random() * 0.007; // 稍微加快消散速度
            const riseSpeed = 0.015 + Math.random() * 0.02; // 稍微减慢上升速度

            smoke.scale.setScalar(scale);

            // 使用单一的animateSmoke函数引用，便于清理
            let animationId = null;
            const animateSmoke = () => {
                scale += scaleSpeed;
                opacity -= opacityDecay;
                
                smoke.scale.setScalar(scale);
                smokeMaterial.opacity = Math.max(0, opacity);
                
                // 简化运动
                smoke.position.y += riseSpeed;
                smoke.position.x += (Math.random() - 0.5) * 0.02;
                smoke.position.z += (Math.random() - 0.5) * 0.02;

                // 检查是否继续动画
                if (opacity > 0 && scale < maxScale) {
                    animationId = requestAnimationFrame(animateSmoke);
                } else {
                    // 清理动画帧
                    if (animationId) {
                        cancelAnimationFrame(animationId);
                    }
                    this.game.sceneManager.remove(smoke);
                    smokeGeometry.dispose();
                    smokeMaterial.dispose();
                }
            };

            // 减少错开时间
            setTimeout(() => animateSmoke(), i * 30);
        }
    }

    // 创建建筑物被击中的特效

    createBuildingHitEffect(position) {
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        if (now - this.lastBuildingHitEffect < this.buildingEffectCooldown) return;
        this.lastBuildingHitEffect = now;
        const debrisIntensity = 0.02;  // 进一步减小碎片强度
        const smokeIntensity = 0.02;   // 进一步减小烟雾强度
        const sparkIntensity = 0.02;   // 进一步减小火花强度

        // Use softer feedback for static island hits
        this.createDebrisEffect(position, debrisIntensity);
        this.createSmokeEffect(position, smokeIntensity);
        this.createSparkEffect(position, sparkIntensity);
    }

    // 创建船只被击中的特效（可调强度）
    createShipHitEffect(position, opts = {}) {
        const intensity = Math.max(0.2, Math.min(1.3, opts.intensity ?? 0.8));
        // 创建木材碎片、黑烟、火花，按强度缩放
        this.createShipDebrisEffect(position, intensity);
        this.createShipSmokeEffect(position, intensity);
        this.createShipSparkEffect(position, intensity);

    }

    createShipDebrisEffect(position, intensity = 1.0) {
        // 创建优化的木材碎片 - 减少粒子数量
        const debrisCount = Math.max(1, Math.round(1 + 3 * intensity)); // 减少到1-4个碎片
        for (let i = 0; i < debrisCount; i++) {
            // 使用简化的几何体
            const debrisGeometry = new THREE.BoxGeometry(
                (Math.random() * 0.6 + 0.2) * (0.4 + 0.3 * intensity),
                (Math.random() * 0.4 + 0.1) * (0.4 + 0.3 * intensity),
                (Math.random() * 0.8 + 0.3) * (0.4 + 0.3 * intensity),
                1, 1, 1 // 减少细分度
            );
            const debrisMaterial = new THREE.MeshLambertMaterial({
                color: Math.random() > 0.6 ? 0x8B4513 : 0xDEB887
            });

            const debris = new THREE.Mesh(debrisGeometry, debrisMaterial);
            debris.position.copy(position);
            debris.position.y += Math.random() * 2 + 0.5; // 减小初始高度

            this.game.sceneManager.add(debris);

            // 优化的碎片动画
            const velScale = 0.4 + 0.4 * intensity;
            const velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 20 * velScale,
                (Math.random() * 15 + 6) * velScale,
                (Math.random() - 0.5) * 20 * velScale
            );

            let time = 0;
            let animationId = null;
            const animateDebris = () => {
                time += 0.02;

                // 简化运动计算
                debris.position.x += velocity.x * 0.02;
                debris.position.z += velocity.z * 0.02;
                debris.position.y += velocity.y * 0.02;
                velocity.y -= 0.35; // 稍微调整重力

                // 简化旋转效果
                const rotScale = 0.3 + 0.5 * intensity;
                debris.rotation.x += 0.1 * rotScale;
                debris.rotation.y += 0.15 * rotScale;

                if (debris.position.y > 0 && time < 3) { // 缩短生命周期
                    animationId = requestAnimationFrame(animateDebris);
                } else {
                    // 清理动画帧引用
                    if (animationId) {
                        cancelAnimationFrame(animationId);
                    }
                    this.game.sceneManager.remove(debris);
                    debrisGeometry.dispose();
                    debrisMaterial.dispose();
                }
            };

            animateDebris();
        }
    }

    createShipSmokeEffect(position, intensity = 1.0) {
        // 创建优化的黑烟云 - 减少粒子数量和性能开销
        const smokeCount = Math.max(1, Math.round(1 + 2 * intensity)); // 进一步减少到1-3个粒子
        for (let i = 0; i < smokeCount; i++) {
            // 使用更低的几何体细分度
            const smokeGeometry = new THREE.SphereGeometry((0.8 + Math.random() * 0.5) * (0.5 + 0.4 * intensity), 4, 3);
            const smokeMaterial = new THREE.MeshBasicMaterial({
                color: 0x333333,
                transparent: true,
                opacity: 0.25 + 0.2 * intensity // 稍微降低透明度
            });

            const smoke = new THREE.Mesh(smokeGeometry, smokeMaterial);
            smoke.position.copy(position);
            smoke.position.x += (Math.random() - 0.5) * 2; // 进一步减小散布范围
            smoke.position.z += (Math.random() - 0.5) * 2;
            smoke.position.y += Math.random() * 1.5 + 0.5; // 减小高度

            this.game.sceneManager.add(smoke);

            // 优化的烟雾动画
            let scale = 0.12 * (0.7 + 0.4 * intensity);
            let opacity = 0.25 + 0.2 * intensity;
            let animationId = null;
            
            const animateSmoke = () => {
                scale += 0.04; // 稍微减慢扩展速度
                opacity -= 0.025; // 稍微加快消散速度
                smoke.scale.setScalar(scale);
                smokeMaterial.opacity = Math.max(0, opacity);
                smoke.position.y += 0.08; // 减慢上升速度

                // 减少随机计算
                if (Math.random() > 0.7) {
                    smoke.position.x += (Math.random() - 0.5) * 0.1;
                    smoke.position.z += (Math.random() - 0.5) * 0.1;
                }

                if (opacity > 0) {
                    animationId = requestAnimationFrame(animateSmoke);
                } else {
                    if (animationId) {
                        cancelAnimationFrame(animationId);
                    }
                    this.game.sceneManager.remove(smoke);
                    smokeGeometry.dispose();
                    smokeMaterial.dispose();
                }
            };

            setTimeout(() => animateSmoke(), i * 80); // 减少错开时间
        }
    }

    createShipSparkEffect(position, intensity = 1.0) {
        // 创建优化的金属火花 - 减少粒子数量
        const sparkCount = Math.max(2, Math.round(2 + 5 * intensity)); // 减少到2-7个火花
        for (let i = 0; i < sparkCount; i++) {
            // 使用简化的几何体
            const sparkGeometry = new THREE.SphereGeometry(0.12, 4, 3);
            const sparkMaterial = new THREE.MeshBasicMaterial({
                color: Math.random() > 0.3 ? 0xFF6600 : 0xFFFF00,
                transparent: true,
                opacity: 1
            });

            const spark = new THREE.Mesh(sparkGeometry, sparkMaterial);
            spark.position.copy(position);

            this.game.sceneManager.add(spark);

            // 优化的火花动画
            const velScale = 0.4 + 0.5 * intensity;
            const velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 30 * velScale,
                (Math.random() * 20 + 6) * velScale,
                (Math.random() - 0.5) * 30 * velScale
            );

            let life = 0.8; // 稍微缩短生命周期
            let animationId = null;
            const animateSpark = () => {
                life -= 0.035; // 稍微加快消散速度

                // 简化运动
                spark.position.x += velocity.x * 0.02;
                spark.position.z += velocity.z * 0.02;
                spark.position.y += velocity.y * 0.02;
                velocity.y -= 0.9; // 稍微调整重力

                // 简化闪烁效果
                sparkMaterial.opacity = life * (0.5 + Math.random() * 0.5);

                if (life > 0 && spark.position.y > position.y - 1.5) { // 减小检测范围
                    animationId = requestAnimationFrame(animateSpark);
                } else {
                    // 清理动画帧引用
                    if (animationId) {
                        cancelAnimationFrame(animationId);
                    }
                    this.game.sceneManager.remove(spark);
                    sparkGeometry.dispose();
                    sparkMaterial.dispose();
                }
            };

            animateSpark();
        }
    }

    // 轻量火焰效果（Sprite 粒子，着火感更明显）
    ensureFireTexture() {
        if (this._fireTexture) return this._fireTexture;
        const canvas = document.createElement('canvas');
        canvas.width = 64; canvas.height = 64;
        const ctx = canvas.getContext('2d');
        const grad = ctx.createRadialGradient(32, 48, 6, 32, 32, 28);
        grad.addColorStop(0.0, 'rgba(255,255,180,1)');
        grad.addColorStop(0.4, 'rgba(255,170,0,0.9)');
        grad.addColorStop(0.7, 'rgba(255,80,0,0.7)');
        grad.addColorStop(1.0, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0,0,64,64);
        const tex = new THREE.CanvasTexture(canvas);
        tex.colorSpace = THREE.SRGBColorSpace;
        this._fireTexture = tex;
        return tex;
    }

    createShipFireEffect(position, intensity = 0.6, options = {}) {
        const parentMesh = options.attachTo || this.mesh;
        const texture = this.ensureFireTexture();

        // 每条船最多存在3个着火精灵
        if (parentMesh) {
            if (!parentMesh.userData) parentMesh.userData = {};
            if (!parentMesh.userData.fireSprites) parentMesh.userData.fireSprites = [];
        }
        const list = parentMesh ? parentMesh.userData.fireSprites : null;
        const maxCount = 3;
        const available = list ? Math.max(0, maxCount - list.length) : 3;
        if (available <= 0) return;

        // 固定最多3个，集中在命中点附近
        const count = available; // 只补足到3个
        for (let i = 0; i < count; i++) {
            const material = new THREE.SpriteMaterial({
                map: texture,
                transparent: true,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
                opacity: 1.0
            });
            const sprite = new THREE.Sprite(material);

            // 将世界坐标转换为父节点的本地坐标，确保跟随船体
            const localPos = position.clone();
            if (parentMesh) parentMesh.worldToLocal(localPos);
            sprite.position.copy(localPos);
            sprite.position.x += (Math.random() - 0.5) * 0.5; // 更集中
            sprite.position.z += (Math.random() - 0.5) * 0.5;
            sprite.position.y += 0.4 + Math.random() * 0.6;

            // 基础尺寸更大，纵向更明显
            const base = 1.0 + intensity * 0.8;
            sprite.scale.set(base * (0.7 + Math.random()*0.5), base * (1.2 + Math.random()*0.5), 1);
            if (parentMesh) parentMesh.add(sprite); else this.game.sceneManager.add(sprite);
            if (list) list.push(sprite);

            // 更久：寿命更长，衰减更慢
            // 需求：时间缩短一些，避免炸太开
            let life = 0.9 + Math.random() * 0.4; // ~0.9 - 1.3s
            const rise = 0.55 + Math.random() * 0.45;
            const spin = (Math.random() - 0.5) * 0.8;
            const animate = () => {
                life -= 0.02; // 更快消退
                const flicker = 0.8 + Math.random() * 0.4; // 闪烁
                sprite.material.opacity = Math.max(0, life) * flicker;
                sprite.position.y += rise * 0.02;
                sprite.scale.y *= 1.01; // 轻微拉伸
                sprite.scale.x *= 0.997;
                // 精灵自转用材质旋转
                sprite.material.rotation += spin * 0.02;
                if (life > 0) {
                    requestAnimationFrame(animate);
                } else {
                    if (sprite.parent) sprite.parent.remove(sprite); else this.game.sceneManager.remove(sprite);
                    if (list) {
                        const idx = list.indexOf(sprite);
                        if (idx >= 0) list.splice(idx, 1);
                    }
                    material.dispose();
                }
            };
            animate();
        }
    }


    createDebrisEffect(position, intensity = 1.0) {
        // 减小碎片效果的强度和数量，使烟尘更小
        const clampedIntensity = Math.max(0.02, Math.min(intensity * 0.6, 0.6)); // 降低强度上限和最小值
        const debrisCount = Math.max(1, Math.round(THREE.MathUtils.lerp(1, 5, clampedIntensity))); // 减少碎片数量

        for (let i = 0; i < debrisCount; i++) {
            const sizeScale = THREE.MathUtils.lerp(0.2, 0.6, clampedIntensity); // 减小碎片大小
            const debrisGeometry = new THREE.BoxGeometry(
                (Math.random() * 0.3 + 0.1) * sizeScale,
                (Math.random() * 0.3 + 0.1) * sizeScale,
                (Math.random() * 0.3 + 0.1) * sizeScale
            );
            const debrisMaterial = new THREE.MeshLambertMaterial({
                color: Math.random() * 0x888888 + 0x333333 // Random gray tone
            });

            const debris = new THREE.Mesh(debrisGeometry, debrisMaterial);
            debris.position.copy(position);
            debris.position.y += Math.random() * THREE.MathUtils.lerp(0.3, 1.5, clampedIntensity) + THREE.MathUtils.lerp(0.1, 0.6, clampedIntensity); // 减小初始高度

            this.game.sceneManager.add(debris);

            const horizontalSpeed = THREE.MathUtils.lerp(0.8, 10, clampedIntensity); // 降低水平速度
            const liftBase = THREE.MathUtils.lerp(0.3, 4.0, clampedIntensity); // 降低初始上升力
            const liftRange = THREE.MathUtils.lerp(0.6, 5.0, clampedIntensity); // 降低上升力范围
            const velocity = new THREE.Vector3(
                (Math.random() - 0.5) * horizontalSpeed,
                Math.random() * liftRange + liftBase,
                (Math.random() - 0.5) * horizontalSpeed
            );
            const gravity = THREE.MathUtils.lerp(0.03, 0.2, clampedIntensity); // 降低重力影响
            const rotationStep = THREE.MathUtils.lerp(0.005, 0.1, clampedIntensity); // 降低旋转速度
            const lifespan = THREE.MathUtils.lerp(0.5, 2.0, clampedIntensity); // 缩短生命周期

            let time = 0;
            const animateDebris = () => {
                time += 0.02;

                // Parabolic arc
                debris.position.x += velocity.x * 0.02;
                debris.position.z += velocity.z * 0.02;
                debris.position.y += velocity.y * 0.02;
                velocity.y -= gravity;

                // Slow spin
                debris.rotation.x += rotationStep;
                debris.rotation.y += rotationStep * 1.5;
                debris.rotation.z += rotationStep * 0.6;

                if (debris.position.y > 0 && time < lifespan) {
                    requestAnimationFrame(animateDebris);
                } else {
                    this.game.sceneManager.remove(debris);
                    debrisGeometry.dispose();
                    debrisMaterial.dispose();
                }
            };

            animateDebris();
        }
    }


    createSmokeEffect(position, intensity = 1.0) {
        // 减小烟雾效果的强度和数量，使烟尘更小
        const clampedIntensity = Math.max(0.02, Math.min(intensity * 0.6, 0.6)); // 降低强度上限和最小值
        const smokeCount = Math.max(1, Math.round(THREE.MathUtils.lerp(1, 3, clampedIntensity))); // 减少烟雾数量
        const fadeStep = THREE.MathUtils.lerp(0.06, 0.025, clampedIntensity); // 加快消散速度
        const scaleStep = THREE.MathUtils.lerp(0.01, 0.03, clampedIntensity); // 减慢扩展速度
        const riseSpeed = THREE.MathUtils.lerp(0.02, 0.08, clampedIntensity); // 降低上升速度
        const radiusBase = THREE.MathUtils.lerp(0.4, 1.0, clampedIntensity); // 减小烟雾基础大小
        const opacityBase = THREE.MathUtils.lerp(0.15, 0.4, clampedIntensity); // 降低基础透明度
        const initialScale = THREE.MathUtils.lerp(0.2, 0.4, clampedIntensity); // 减小初始大小
        const driftRange = THREE.MathUtils.lerp(0.3, 1.5, clampedIntensity); // 减小扩散范围
        const heightJitter = THREE.MathUtils.lerp(0.4, 2.0, clampedIntensity); // 减小高度变化
        const baseLift = THREE.MathUtils.lerp(0.3, 1.0, clampedIntensity); // 降低基础高度
        const delayStep = THREE.MathUtils.lerp(50, 100, clampedIntensity); // 减小错开时间

        for (let i = 0; i < smokeCount; i++) {
            const smokeGeometry = new THREE.SphereGeometry(radiusBase + Math.random() * 0.3 * clampedIntensity);
            const smokeMaterial = new THREE.MeshBasicMaterial({
                color: 0x666666,
                transparent: true,
                opacity: opacityBase
            });

            const smoke = new THREE.Mesh(smokeGeometry, smokeMaterial);
            smoke.position.copy(position);
            smoke.position.x += (Math.random() - 0.5) * driftRange;
            smoke.position.z += (Math.random() - 0.5) * driftRange;
            smoke.position.y += Math.random() * heightJitter + baseLift;
            smoke.scale.setScalar(initialScale);

            this.game.sceneManager.add(smoke);

            let scale = initialScale;
            let opacity = opacityBase;
            const animateSmoke = () => {
                scale += scaleStep;
                opacity = Math.max(0, opacity - fadeStep);
                smoke.scale.setScalar(scale);
                smokeMaterial.opacity = opacity;
                smoke.position.y += riseSpeed;

                // 添加轻微的水平漂移
                smoke.position.x += (Math.random() - 0.5) * 0.05;
                smoke.position.z += (Math.random() - 0.5) * 0.05;

                if (opacity > 0) {
                    requestAnimationFrame(animateSmoke);
                } else {
                    this.game.sceneManager.remove(smoke);
                    smokeGeometry.dispose();
                    smokeMaterial.dispose();
                }
            };

            setTimeout(() => animateSmoke(), i * delayStep);
        }
    }


    createSparkEffect(position, intensity = 1.0) {
        // 减小火花效果的强度和数量，使烟尘更小
        const clampedIntensity = Math.max(0.02, Math.min(intensity * 0.6, 0.6)); // 降低强度上限和最小值
        const sparkCount = Math.max(1, Math.round(THREE.MathUtils.lerp(1, 8, clampedIntensity))); // 减少火花数量
        const velocityScale = THREE.MathUtils.lerp(1.5, 15, clampedIntensity); // 降低速度
        const upwardBase = THREE.MathUtils.lerp(0.5, 3, clampedIntensity); // 降低初始上升力
        const lifeDecay = THREE.MathUtils.lerp(0.08, 0.05, clampedIntensity); // 加快消散速度
        const sparkSize = THREE.MathUtils.lerp(0.02, 0.06, clampedIntensity); // 减小火花大小
        const startLife = THREE.MathUtils.lerp(0.2, 0.6, clampedIntensity); // 缩短生命周期
        const gravity = THREE.MathUtils.lerp(0.3, 0.6, clampedIntensity); // 调整重力影响

        for (let i = 0; i < sparkCount; i++) {
            const sparkGeometry = new THREE.SphereGeometry(sparkSize);
            const sparkMaterial = new THREE.MeshBasicMaterial({
                color: Math.random() > 0.5 ? 0xFFAA00 : 0xFF4400,
                transparent: true,
                opacity: 1
            });

            const spark = new THREE.Mesh(sparkGeometry, sparkMaterial);
            spark.position.copy(position);

            this.game.sceneManager.add(spark);

            const velocity = new THREE.Vector3(
                (Math.random() - 0.5) * velocityScale,
                Math.random() * THREE.MathUtils.lerp(0.8, 10, clampedIntensity) + upwardBase,
                (Math.random() - 0.5) * velocityScale
            );

            let life = startLife;
            const animateSpark = () => {
                life -= lifeDecay;

                spark.position.x += velocity.x * 0.02;
                spark.position.z += velocity.z * 0.02;
                spark.position.y += velocity.y * 0.02;
                velocity.y -= gravity;

                sparkMaterial.opacity = Math.max(0, life * (0.5 + Math.sin(Date.now() * 0.1) * 0.5));

                if (life > 0 && spark.position.y > position.y - 0.5) { // 减小检测范围
                    requestAnimationFrame(animateSpark);
                } else {
                    this.game.sceneManager.remove(spark);
                    sparkGeometry.dispose();
                    sparkMaterial.dispose();
                }
            };

            animateSpark();
        }
    }

    createSplash(position) {
        // 保留水花效果用于其他地方
        this.createExplosion(position);
    }

    // Use special ability (number keys 1-5)
    useAbility(abilityIndex) {
        switch (abilityIndex) {
            case 0: // Key 1 - Speed boost
                this.speedBoost();
                break;
            case 1: // Key 2 - Rapid fire
                this.rapidFire();
                break;
            case 2: // Key 3 - TODO: Deploy mine
                break;
            case 3: // Key 4 - TODO: Torpedo
                break;
            case 4: // Key 5 - TODO: Special attack
                break;
        }
    }

    speedBoost() {
        const originalSpeed = this.maxVelocity;
        this.maxVelocity *= 2;

        // Reset after 3 seconds
        setTimeout(() => {
            this.maxVelocity = originalSpeed;
        }, 3000);
    }

    rapidFire() {
        const originalCooldown = this.cannonCooldown;
        this.cannonCooldown = 0.2;

        // Reset after 5 seconds
        setTimeout(() => {
            this.cannonCooldown = originalCooldown;
        }, 5000);
    }

    // Damage handling
    takeDamage(damage = 1, source = null) {
        const oldHealth = this.health;
        this.health = Math.max(0, this.health - damage);
        if (source) {
            this.lastAttacker = source;
        }
        console.log(`Player took ${damage} damage: ${oldHealth} -> ${this.health} / ${this.maxHealth}`); // 调试信息
        this.updateHealthBar();

        // 显示伤害数值（只在敌船攻击时显示）
        if (this.mesh && source && source.faction === 'enemy') {
            this.showDamageNumber(damage, new THREE.Vector3().copy(this.mesh.position), true);
        }

        // Visual feedback
        this.flashDamage();

        // Spawn hit effect on non-lethal hits as well (throttled)
        if (this.mesh) {
            const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            if (now - this.lastHitEffect > this.hitEffectCooldown) {
                const ratio = this.maxHealth ? this.health / this.maxHealth : 0.5;
                const lethal = this.health <= 0;
                const intensity = lethal ? 0.9 : Math.max(0.15, Math.min(0.7, 0.15 + (1 - ratio) * 0.4));
                this.createShipHitEffect(new THREE.Vector3().copy(this.mesh.position), { intensity, lethal });
                if (this.health > 0) {
                    this.createShipFireEffect(new THREE.Vector3().copy(this.mesh.position), intensity * 0.9);
                }
                this.lastHitEffect = now;
            }
        }

        if (this.health <= 0) {
            this.destroy();
        }

    }

    // 显示伤害数值
    showDamageNumber(damage, position, isReceiving = false) {
        if (!this.game || !this.game.sceneManager) return;

        // 创建canvas用于绘制数字
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 128;
        const context = canvas.getContext('2d');

        // 绘制文字 - 更大字体
        context.font = 'bold 100px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';

        // 设置阴影 - 更明显的阴影
        context.shadowColor = 'rgba(0, 0, 0, 1.0)';
        context.shadowBlur = 12;
        context.shadowOffsetX = 5;
        context.shadowOffsetY = 5;

        // 设置颜色：受伤为红色，造成伤害为鲜艳黄色
        context.fillStyle = isReceiving ? '#FF0000' : '#FFD700';
        // 去掉负号，直接显示数字
        context.fillText(`${Math.round(damage)}`, canvas.width / 2, canvas.height / 2);

        // 创建sprite
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthWrite: false
        });
        const sprite = new THREE.Sprite(material);

        // 设置位置（在船上方）- 更大尺寸
        sprite.position.copy(position);
        sprite.position.y += 5;
        sprite.scale.set(6, 3, 1); // 从4,2增加到6,3

        // 设置为 layer 1，避免水面倒影
        sprite.layers.set(1);

        this.game.sceneManager.add(sprite);

        // 动画：上升并淡出 - 消失更慢
        let life = 1.0;
        const riseSpeed = 0.02; // 稍慢上升
        const fadeSpeed = 0.01; // 减半淡出速度，持续时间翻倍

        const animate = () => {
            life -= fadeSpeed;
            sprite.position.y += riseSpeed;
            sprite.material.opacity = Math.max(0, life);
            sprite.scale.x *= 1.02; // 轻微放大
            sprite.scale.y *= 1.02;

            if (life > 0) {
                requestAnimationFrame(animate);
            } else {
                this.game.sceneManager.remove(sprite);
                material.dispose();
                texture.dispose();
            }
        };
        animate();
    }

    flashDamage() {
        if (!this.mesh || this.isFlashing) return;

        this.isFlashing = true;

        // Store original colors and set flash color for all meshes in the group
        const originalColors = [];

        this.mesh.traverse((child) => {
            if (child.isMesh && child.material && child.material.color) {
                originalColors.push({
                    material: child.material,
                    color: child.material.color.getHex()
                });
                child.material.color.setHex(0xFF0000); // Red flash for player damage
            }
        });

        // Restore original colors after flash
        setTimeout(() => {
            originalColors.forEach(({ material, color }) => {
                if (material && material.color) {
                    material.color.setHex(color);
                }
            });
            this.isFlashing = false;
        }, 200);
    }

    heal(amount) {
        this.health = Math.min(this.maxHealth, this.health + amount);
        this.updateHealthBar();
    }

    // 血包恢复到满血
    healToFull() {
        this.health = this.maxHealth;
        this.updateHealthBar();
        console.log(`Player healed to full health: ${this.health}/${this.maxHealth}`); // 调试信息
    }

    destroy() {
        console.log('Player ship destroyed, triggering respawn...'); // 调试信息

        // 防止重复触发
        if (this.isDestroyed) {
            console.warn('Player already destroyed, skipping');
            return;
        }
        this.isDestroyed = true;

        // Create explosion effect
        if (this.mesh && this.mesh.position) {
            this.createExplosion(this.mesh.position);
        }

        // Trigger respawn instead of game over
        if (this.game && this.game.respawnPlayer) {
            this.game.respawnPlayer();
        }
    }

    // Reset player ship for respawn
    respawn(position) {
        // Ensure position is a THREE.Vector3
        let spawnPosition = position;
        if (!spawnPosition || typeof spawnPosition !== 'object' ||
            typeof spawnPosition.x !== 'number' || typeof spawnPosition.z !== 'number') {
            spawnPosition = new THREE.Vector3(0, 0.1, 0);
        }

        console.log('Respawning player at:', spawnPosition); // 调试信息

        // 重置销毁标志
        this.isDestroyed = false;

        this.clearAttackTarget();

        // Reset health
        this.health = this.maxHealth;
        this.updateHealthBar();

        // Reset position
        this.position = new THREE.Vector3().copy(spawnPosition);
        this.targetPosition.copy(spawnPosition);
        this.smoothedTarget.copy(spawnPosition);

        // Reset physics body position and make sure it's enabled
        if (this.body) {
            this.body.position.set(spawnPosition.x, spawnPosition.y, spawnPosition.z);
            this.body.velocity.set(0, 0, 0);
            this.body.angularVelocity.set(0, 0, 0);
            this.body.quaternion.set(0, 0, 0, 1); // Reset rotation
            if (typeof this.body.wakeUp === 'function') {
                this.body.wakeUp();
            }

            // 确保物理体已添加到世界中
            if (this.game && this.game.physicsSystem && !this.game.physicsSystem.world.bodies.includes(this.body)) {
                this.game.physicsSystem.addBody(this.body);
                console.log('Re-added physics body to world'); // 调试信息
            }
        }

        // Reset mesh position and make sure it's visible
        if (this.mesh) {
            this.mesh.position.copy(spawnPosition);
            this.mesh.quaternion.set(0, 0, 0, 1); // Reset rotation
            this.mesh.visible = true; // 确保可见

            // 确保网格已添加到场景中
            if (this.game && this.game.sceneManager && !this.mesh.parent) {
                this.game.sceneManager.add(this.mesh);
                console.log('Re-added mesh to scene'); // 调试信息
            }
        }

        // 重建血条，避免遗留的受伤视觉状态
        this.cleanupHealthBar();
        this.createHealthBar();

        this.updateMovementProfile(this.level);

        console.log('Respawn complete'); // 调试信息
    }

    // Collision handling
    handleCollision(event) {
        const otherBody = event.target === this.body ? event.body : event.target;

        // 只通过 userData 判断炮弹，避免重复结算伤害
        if (otherBody.userData) {
            switch (otherBody.userData.type) {
                case 'enemy':
                    // 船体对船体：仅物理阻挡，不掉血
                    break;
                case 'cannonball':
                    // 非自身发射的炮弹才计算伤害，且不是友军炮弹
                    if (otherBody.userData.shooter !== this.body && !otherBody.userData.isAllyProjectile) {
                        const damage = otherBody.userData.damage ?? 1;
                        this.takeDamage(damage);
                    }
                    break;
            }
        }
    }

    // Get current stats for UI
    getStats() {
        return {
            health: this.health,
            maxHealth: this.maxHealth,
            position: this.mesh ? this.mesh.position : new THREE.Vector3(),
            isMoving: this.isMoving,
            canFire: this.lastFireTime <= 0
        };
    }

    // 清理旧的血条
    cleanupHealthBar() {
        if (this.healthBarContainer) {
            // 移除所有子对象
            while (this.healthBarContainer.children.length > 0) {
                const child = this.healthBarContainer.children[0];
                this.healthBarContainer.remove(child);
                // 清理几何体和材质
                if (child.geometry) child.geometry.dispose();
                if (child.material) child.material.dispose();
            }
            // 从父对象中移除
            if (this.healthBarContainer.parent) {
                this.healthBarContainer.parent.remove(this.healthBarContainer);
            }
            this.healthBarContainer = null;
        }
        this.healthSegments = [];
        this.healthSegmentCount = 0;
    }

    // Health bar creation and management
    createHealthBar() {
        console.log(`Creating health bar with maxHealth: ${this.maxHealth}, level: ${this.level}`); // 调试信息

        // Create health bar container
        this.healthBarContainer = new THREE.Group();

        const meshScaleX = this.mesh?.scale?.x ?? 1;
        const meshScaleY = this.mesh?.scale?.y ?? 1;
        const meshScaleZ = this.mesh?.scale?.z ?? 1;

        // 根据船只等级调整血条高度（世界空间）
        let healthBarHeight = 6; // 默认高度
        if (this.level >= 3) {
            healthBarHeight = 8; // 巡洋舰更高
        } else if (this.level >= 2) {
            healthBarHeight = 7; // 大船中等高度
        }

        const localHeight = meshScaleY !== 0 ? healthBarHeight / meshScaleY : healthBarHeight;
        this.healthBarContainer.position.set(0, localHeight, 0); // Above ship

        const inverseScaleX = meshScaleX !== 0 ? 1 / meshScaleX : 1;
        const inverseScaleY = meshScaleY !== 0 ? 1 / meshScaleY : 1;
        const inverseScaleZ = meshScaleZ !== 0 ? 1 / meshScaleZ : 1;
        this.healthBarContainer.scale.set(inverseScaleX, inverseScaleY, inverseScaleZ);

        this.mesh.add(this.healthBarContainer);

        const segmentCount = 6;
        this.healthSegmentCount = segmentCount;

        const segmentWidth = 0.7;
        const segmentSpacing = 0.9;
        const totalWidth = segmentCount * segmentSpacing;

        console.log(`Creating health bar: maxHealth=${this.maxHealth}, segments=${segmentCount}, totalWidth=${totalWidth}`); // 调试信息

        // Background bar (gray) - 宽度根据血量段数调整
        const backgroundGeometry = new THREE.PlaneGeometry(totalWidth, 0.4);
        const backgroundMaterial = new THREE.MeshBasicMaterial({
            color: 0x333333,
            transparent: true,
            opacity: 0.8
        });
        this.healthBarBackground = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
        this.healthBarContainer.add(this.healthBarBackground);

        // Health segments (green)
        this.healthSegments = [];
        for (let i = 0; i < segmentCount; i++) {
            const segmentGeometry = new THREE.PlaneGeometry(segmentWidth, 0.32);
            const segmentMaterial = new THREE.MeshBasicMaterial({
                color: 0x00FF00,
                transparent: true,
                opacity: 0.9
            });
            const segment = new THREE.Mesh(segmentGeometry, segmentMaterial);

            // 居中排列血条段
            const startX = -(totalWidth / 2) + (segmentSpacing / 2); // 从背景左边开始，第一段居中
            const baseX = startX + (i * segmentSpacing);
            segment.position.x = baseX;
            segment.position.z = 0.01; // Slightly in front of background

            segment.userData = {
                baseX,
                segmentWidth
            };

            this.healthBarContainer.add(segment);
            this.healthSegments.push(segment);
        }

        // Health bar label (optional)
        this.updateHealthBar();

    }

    updateHealthBar() {
        if (!this.healthSegments) return;

        // Update segment visibility based on current health percentage
        // 血条显示百分比，不管实际血量数值多少
        const segmentCount = this.healthSegmentCount || this.healthSegments.length || 1;
        const healthPercent = this.maxHealth > 0 ? Math.max(0, this.health / this.maxHealth) : 0;
        const activeSegmentValue = segmentCount * healthPercent;

        console.log(`Health bar update: ${this.health}/${this.maxHealth} (${(healthPercent*100).toFixed(1)}%) with ${segmentCount} segments`); // 调试信息

        let segmentColor = 0x00FF00; // Green

        if (healthPercent <= 0.3) {
            segmentColor = 0xFF0000; // Red
        } else if (healthPercent <= 0.6) {
            segmentColor = 0xFFAA00; // Orange
        }

        for (let i = 0; i < this.healthSegments.length; i++) {
            const segment = this.healthSegments[i];
            const fillAmount = Math.max(0, Math.min(1, activeSegmentValue - i));

            if (fillAmount <= 0) {
                segment.visible = false;
                continue;
            }

            segment.visible = true;
            segment.scale.x = fillAmount;

            if (segment.userData && typeof segment.userData.baseX === 'number' && segment.userData.segmentWidth) {
                const baseX = segment.userData.baseX;
                const width = segment.userData.segmentWidth;
                segment.position.x = baseX - (width * (1 - fillAmount)) / 2;
            }

            segment.material.color.setHex(segmentColor);
        }

    }

    // Cleanup
    dispose() {
        if (this.mesh) {
            // Dispose geometries and materials
            this.mesh.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(material => material.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
        }

        if (this.body && this.game && this.game.physicsSystem) {
            this.game.physicsSystem.removeBody(this.body);
        }

    }

    // 获取玩家金币数
    getPlayerCoins() {
        if (this.game && this.game.coinSystem) {
            return this.game.coinSystem.getPlayerCoins();
        }
        return 0;
    }

    // 检查是否可以升级
    checkUpgrade() {
        if (this.isUpgrading) return false;

        const maxLevel = LEVEL_CAP;
        if (this.level >= maxLevel) {
            return false; // 静默返回，不显示提示
        }

        const nextLevel = this.level + 1;
        const requiredCoins = getUpgradeCostForLevel(nextLevel) || 0;
        const coinSystem = this.game?.coinSystem;

        if (!coinSystem) return false;

        const availableCoins = coinSystem.getPlayerCoins();
        if (availableCoins < requiredCoins) {
            return false;
        }

        const spent = requiredCoins > 0 ? coinSystem.spendCoins(requiredCoins) : true;
        if (!spent) {
            return false;
        }

        if (this.game.rankingUI) {
            this.game.rankingUI.updateCoinDisplay(coinSystem.getPlayerCoins());
        }

        console.log(`Upgrading to level ${nextLevel} using ${requiredCoins} coins`);
        this.upgradeShip(nextLevel);
        return true;
    }

    // 升级船只
    async upgradeShip(newLevel) {
        if (this.isUpgrading) return;
        
        console.log(`Starting upgrade to level ${newLevel}`); // 调试信息
        
        this.isUpgrading = true;

        // 停止船只运动，避免动画期间飘移
        if (this.body) {
            this.body.velocity.set(0, 0, 0);
            this.body.angularVelocity.set(0, 0, 0);
        }

        // 显示升级动画（异步，不阻塞）
        this.showUpgradeAnimation();

        // 异步等待动画，不阻塞主线程
        setTimeout(() => {
            this.completeUpgrade(newLevel);
        }, 1000);
        
        // 保存当前状态
        const oldPosition = this.body.position.clone();
        const oldRotation = this.body.quaternion.clone();
        const oldTargetPosition = this.targetPosition.clone();
        // 不保存速度，升级后从静止开始
        
        // 移除旧的网格和物理体
        if (this.mesh) {
            this.game.sceneManager.remove(this.mesh);
        }
        if (this.body) {
            this.game.physicsSystem.removeBody(this.body);
        }
        
        // 更新等级
        const oldLevel = this.level;
        this.level = newLevel;
        console.log(`Level changed from ${oldLevel} to ${newLevel}`); // 调试信息
        
        // 根据等级选择模型
        const modelName = getPlayerModelForLevel(newLevel);
        console.log(`Loading model: ${modelName}`); // 调试信息

        await this.createMesh(modelName);

        // 将新模型添加到场景中
        if (this.mesh && this.game.sceneManager) {
            this.game.sceneManager.add(this.mesh);
        }

        // 先更新船只属性，再创建物理体
        this.applyLevelStats(newLevel);
        console.log(`Updated ship attributes for level ${newLevel} before creating physics body:`, {
            speed: this.speed,
            cannonballSize: this.cannonballSize,
            cannonballDamage: this.cannonballDamage,
            cannonCooldown: this.cannonCooldown
        });

        // 重新创建物理体（此时属性已更新）
        this.createPhysicsBody();

        // 恢复位置和旋转，升级后从静止开始
        this.body.position.copy(oldPosition);
        this.body.quaternion.copy(oldRotation);
        this.body.velocity.set(0, 0, 0); // 升级后静止
        this.body.angularVelocity.set(0, 0, 0); // 升级后不旋转
        this.mesh.position.copy(oldPosition);
        this.mesh.quaternion.copy(oldRotation);

        // 恢复目标位置
        this.targetPosition.copy(oldTargetPosition);
        this.smoothedTarget.copy(oldTargetPosition);
        
        // 等级变更后血量已由 applyLevelStats 设置
        console.log(`Updated health: ${this.health}/${this.maxHealth} for level ${newLevel}`); // 调试信息

        // 重新创建血条（先清理旧的）
        this.cleanupHealthBar();
        this.createHealthBar();
        this.updateHealthBar();
        
        // 属性更新已在创建物理体之前完成

        // 重新设置相机跟随目标以修复海面动画问题
        if (this.game && this.game.sceneManager) {
            this.game.sceneManager.setFollowTarget(this.mesh);
        }

        // 根据船只大小调整相机偏移以保持在视野中心
        this.adjustCameraForUpgrade(newLevel);
        
        this.isUpgrading = false;
        
        // 显示升级完成提示
        this.showUpgradeCompleteNotification();
        
        console.log(`Upgrade to level ${newLevel} completed`); // 调试信息

        // 检查是否可继续升级（可能一次性攒够多级所需金币）
        this.checkUpgrade();
    }

    applyLevelStats(level) {
        const data = getLevelData(level);
        this.level = data.level;
        this.levelData = data;

        this.maxHealth = data.health;
        this.health = data.health;
        this.cannonballDamage = data.attack;

        this.updateMovementProfile(level);

        if (this.healthSegments && this.healthSegments.length) {
            this.updateHealthBar();
        }
    }

    updateMovementProfile(level) {
        if (level >= 8) {
            this.speed = 22;
            this.acceleration = 60;
            this.cannonballSize = 1.3;
            this.cannonCooldown = 1.1; // 恢复原来的快速射击
            this.autoFireCooldown = 1.6;
            this.moveThreshold = 2.0;
        } else if (level >= 5) {
            this.speed = 19;
            this.acceleration = 54;
            this.cannonballSize = 1.1;
            this.cannonCooldown = 1.3; // 恢复原来的快速射击
            this.autoFireCooldown = 1.9;
            this.moveThreshold = 2.4;
        } else if (level >= 3) {
            this.speed = 17;
            this.acceleration = 50;
            this.cannonballSize = 0.95;
            this.cannonCooldown = 1.5; // 恢复原来的快速射击
            this.autoFireCooldown = 2.1;
            this.moveThreshold = 2.7;
        } else {
            this.speed = 15;
            this.acceleration = 45;
            this.cannonballSize = 0.8;
            this.cannonCooldown = 1.8; // 恢复原来的快速射击
            this.autoFireCooldown = 2.4;
            this.moveThreshold = 3.0;
        }

        this.maxVelocity = this.speed;

        if (this.body) {
            let damping = 0.55;
            if (level >= 5) damping = 0.48;
            if (level >= 8) damping = 0.42;
            this.body.linearDamping = damping;
        }
    }

    // 根据船只等级调整相机偏移（针对缩小后的船只）
    adjustCameraForUpgrade(level) {
        if (!this.game || !this.game.sceneManager) return;

        // 根据船只大小调整相机偏移量（已经缩小一倍）
        let cameraOffset;
        if (level >= 9) {
            cameraOffset = new THREE.Vector3(0, 52, 28);
        } else if (level >= 7) {
            cameraOffset = new THREE.Vector3(0, 48, 26);
        } else if (level >= 4) {
            cameraOffset = new THREE.Vector3(0, 42, 23);
        } else {
            cameraOffset = new THREE.Vector3(0, 35, 20);
        }

        // 应用新的相机偏移
        this.game.sceneManager.setCameraOffset(cameraOffset);

        console.log(`Camera offset adjusted for level ${level}:`, cameraOffset); // 调试信息
    }


    // 显示升级动画
    showUpgradeAnimation() {
        // 创建升级特效
        const effectCount = 20;
        for (let i = 0; i < effectCount; i++) {
            const particleGeometry = new THREE.SphereGeometry(0.2, 8, 6);
            const particleMaterial = new THREE.MeshBasicMaterial({
                color: new THREE.Color().setHSL(Math.random(), 1.0, 0.7),
                transparent: true,
                opacity: 0.8
            });

            const particle = new THREE.Mesh(particleGeometry, particleMaterial);
            particle.position.copy(this.mesh.position);
            particle.position.y += Math.random() * 3;
            particle.position.x += (Math.random() - 0.5) * 4;
            particle.position.z += (Math.random() - 0.5) * 4;

            this.game.sceneManager.add(particle);

            // 粒子动画
            let time = 0;
            const duration = 2.0;
            const startY = particle.position.y;
            const targetY = startY + 5;
            const startScale = 1.0;
            const targetScale = 0.1;

            const animateParticle = () => {
                time += 0.016;
                const progress = Math.min(time / duration, 1.0);

                // 上升动画
                particle.position.y = startY + (targetY - startY) * progress;

                // 缩放动画
                const scale = startScale + (targetScale - startScale) * progress;
                particle.scale.setScalar(scale);

                // 旋转动画
                particle.rotation.x += 0.1;
                particle.rotation.y += 0.15;

                // 透明度变化
                particleMaterial.opacity = 1.0 - progress;

                if (progress < 1.0) {
                    requestAnimationFrame(animateParticle);
                } else {
                    this.game.sceneManager.remove(particle);
                    particleGeometry.dispose();
                    particleMaterial.dispose();
                }
            };

            setTimeout(() => animateParticle(), i * 100);
        }

        // 清除所有旧提醒
        this.clearAllNotifications();

        // 显示升级文本 - 与击杀提示相同的位置和样式
        const upgradeText = document.createElement('div');
        upgradeText.className = 'game-notification'; // 添加标识class
        upgradeText.style.cssText = `
            position: fixed;
            top: 20px;
            left: 20px;
            background: rgba(0, 120, 255, 0.9);
            color: white;
            padding: 15px 25px;
            border-radius: 8px;
            font-size: 18px;
            font-weight: bold;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            animation: fadeInOut 2s ease-in-out;
        `;
        upgradeText.textContent = `🚀 升级到 ${this.level + 1} 级！`;
        document.body.appendChild(upgradeText);

        // 2秒后移除
        setTimeout(() => {
            if (upgradeText.parentNode) {
                upgradeText.parentNode.removeChild(upgradeText);
            }
        }, 2000);
    }

    // 显示升级完成提示
    showUpgradeCompleteNotification() {
        // 清除所有旧提醒
        this.clearAllNotifications();

        // 与击杀提示相同的位置和样式
        const notification = document.createElement('div');
        notification.className = 'game-notification'; // 添加标识class
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            left: 20px;
            background: rgba(0, 200, 100, 0.9);
            color: white;
            padding: 15px 25px;
            border-radius: 8px;
            font-size: 18px;
            font-weight: bold;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            animation: fadeInOut 2s ease-in-out;
        `;
        notification.textContent = `✅ 升级完成！现在是 ${this.level} 级战舰`;
        document.body.appendChild(notification);

        // 2秒后移除
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 2000);
    }

    // 清除所有游戏提醒
    clearAllNotifications() {
        const notifications = document.querySelectorAll('.game-notification');
        notifications.forEach(notification => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        });
    }

    // 添加分数
    addScore(points) {
        this.score += points;
        console.log(`Player addScore: +${points}, Total: ${this.score}, Level: ${this.level}`); // 调试信息
    }

    // 重载createMesh方法以支持模型名称参数
    async createMesh(modelName = 'boat-sail-b') {
        const loader = new GLTFLoader();

        try {
            const gltf = await loader.loadAsync(`/models/GLB format/${modelName}.glb`);
            this.mesh = gltf.scene;

            this.mesh.scale.setScalar(1);
            this.mesh.castShadow = true;
            this.mesh.receiveShadow = true;

            this.mesh.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    if (child.material) {
                        child.material = child.material.clone();
                    }
                }
            });

        } catch (error) {
            console.warn(`Failed to load ${modelName}.glb model, falling back to basic mesh:`, error);
            this.createBasicMesh();
            return;
        }

        this.mesh.userData = { type: 'player', entity: this };
        this.mesh.position.copy(this.position);
    }
}
